import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch, Checkbox,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LinkOff as DisconnectIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { calendarApi, calendarSourcesApi, calendarConnectApi } from '../api/services';
import type { CalendarEvent, CalendarSource } from '../types/models';

export default function CalendarPage() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [connectionBanner, setConnectionBanner] = useState<string | null>(null);

  // Check for ?connected= query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected === 'microsoft' || connected === 'google') {
      const label = connected === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      setConnectionBanner(`${label} connected successfully! Your calendar will sync automatically.`);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadEvents = async (date: string) => {
    setLoading(true);
    try {
      setEvents(await calendarApi.getByDate(date));
    } catch (err) {
      console.error('Failed to load events', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEvents(selectedDate); }, [selectedDate]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const synced = await calendarApi.sync();
      setSyncMessage({ type: 'success', text: `Synced ${synced.length} events from ICS feed` });
      loadEvents(selectedDate);
    } catch (err: any) {
      const data = err.response?.data;
      const msg = typeof data === 'string' ? data : data?.error || data?.message || 'Sync failed — check ICS URL in preferences';
      setSyncMessage({ type: 'error', text: typeof msg === 'string' ? msg : 'Sync failed — check ICS URL in preferences' });
    } finally {
      setSyncing(false);
    }
  };

  const meetingMinutes = events
    .filter(e => !e.isAllDay)
    .reduce((sum, e) => sum + (new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 60000, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Calendar</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync ICS'}
          </Button>
        </Stack>
      </Box>

      {connectionBanner && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />} onClose={() => setConnectionBanner(null)}>
          {connectionBanner}
        </Alert>
      )}
      {syncMessage && <Alert severity={syncMessage.type} sx={{ mb: 2 }} onClose={() => setSyncMessage(null)}>{syncMessage.text}</Alert>}

      <ConnectCalendarCard />

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Events</Typography>
            <Typography variant="h6">{events.length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Meeting Time</Typography>
            <Typography variant="h6">{Math.round(meetingMinutes / 60 * 10) / 10}h</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Priority Meetings</Typography>
            <Typography variant="h6" color="error.main">{events.filter(e => e.isPriorityMeeting || e.isPrivate).length}</Typography>
          </Box>
        </CardContent>
      </Card>

      <CalendarSourcesCard />

      <Stack spacing={1} sx={{ mt: 3 }}>
        {events.map((event) => (
          <Card key={event.id} sx={{
            borderLeft: 3,
            borderColor: event.isPriorityMeeting || event.isPrivate ? 'error.main' : event.isAllDay ? 'warning.main' : 'secondary.main',
          }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 110, color: 'text.secondary' }}>
                  {event.isAllDay ? 'All day' : `${formatTime(event.startTime)} – ${formatTime(event.endTime)}`}
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{event.title}</Typography>
                <Stack direction="row" spacing={0.5}>
                  {(event.isPriorityMeeting || event.isPrivate) && (
                    <Chip icon={<WarningIcon />} label="Priority" size="small" color="error" variant="outlined" sx={{ height: 24 }} />
                  )}
                  {event.isPrivate && <Chip label="Private" size="small" variant="outlined" sx={{ height: 24 }} />}
                </Stack>
              </Box>
              {event.location && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: '126px' }}>{event.location}</Typography>
              )}
            </CardContent>
          </Card>
        ))}
        {events.length === 0 && !loading && (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No calendar events for this date</Typography>
          </Card>
        )}
      </Stack>
    </Box>
  );
}

// Microsoft icon SVG inline
function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

// Google icon SVG inline
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function ProviderIcon({ sourceType }: { sourceType?: string }) {
  if (sourceType === 'Microsoft365') return <MicrosoftIcon />;
  if (sourceType === 'Google') return <GoogleIcon />;
  return <SyncIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
}

function ConnectCalendarCard() {
  const [sources, setSources] = useState<CalendarSource[]>([]);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [isPersonal, setIsPersonal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const loadSources = async () => {
    try {
      const all = await calendarSourcesApi.getAll();
      setSources(all.filter(s => s.sourceType === 'Microsoft365' || s.sourceType === 'Google'));
    } catch (err) {
      console.error('Failed to load sources', err);
    }
  };

  useEffect(() => { loadSources(); }, []);

  const handleConnect = async (provider: 'microsoft' | 'google') => {
    setConnecting(provider);
    setError(null);
    try {
      const name = provider === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      const data = provider === 'microsoft'
        ? await calendarConnectApi.connectMicrosoft({
            name,
            isPersonal,
            eaVisibility: isPersonal ? 'TimeOnly' : undefined,
          })
        : await calendarConnectApi.connectGoogle({
            name,
            isPersonal,
            eaVisibility: isPersonal ? 'TimeOnly' : undefined,
          });
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to start connection';
      setError(typeof msg === 'string' ? msg : 'Failed to start connection');
      setConnecting(null);
    }
  };

  const handleSync = async (sourceId: string) => {
    setSyncingId(sourceId);
    try {
      await calendarConnectApi.sync(sourceId);
      loadSources();
    } catch (err) {
      console.error('Failed to sync', err);
    } finally {
      setSyncingId(null);
    }
  };

  const handleDisconnect = async (sourceId: string) => {
    if (!window.confirm('Disconnect this calendar? All synced events from this source will be removed.')) return;
    setDisconnectingId(sourceId);
    try {
      await calendarConnectApi.disconnect(sourceId);
      loadSources();
    } catch (err) {
      console.error('Failed to disconnect', err);
    } finally {
      setDisconnectingId(null);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Connect Calendar</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<MicrosoftIcon />}
            onClick={() => handleConnect('microsoft')}
            disabled={connecting !== null}
            sx={{
              bgcolor: '#0078D4',
              '&:hover': { bgcolor: '#106EBE' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            {connecting === 'microsoft' ? 'Connecting...' : 'Connect Microsoft 365'}
          </Button>
          <Button
            variant="contained"
            startIcon={<GoogleIcon />}
            onClick={() => handleConnect('google')}
            disabled={connecting !== null}
            sx={{
              bgcolor: '#1A1A1A',
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: '#2D2D2D' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            {connecting === 'google' ? 'Connecting...' : 'Connect Google Calendar'}
          </Button>
        </Stack>

        <FormControlLabel
          control={
            <Checkbox
              checked={isPersonal}
              onChange={e => setIsPersonal(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              This is a personal calendar (EA will see time blocks only, not event details)
            </Typography>
          }
        />

        {/* Connected calendars list */}
        {sources.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Connected Calendars</Typography>
            <Stack spacing={1}>
              {sources.map((source) => (
                <Box
                  key={source.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <ProviderIcon sourceType={source.sourceType} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500}>{source.name}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {source.lastSyncedAt
                          ? `Last synced ${format(new Date(source.lastSyncedAt), 'dd MMM HH:mm')}`
                          : 'Never synced'}
                      </Typography>
                      {source.isPersonal && (
                        <Chip label="Personal" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Stack>
                  </Box>
                  <Chip
                    label={source.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={source.isActive ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ height: 22 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleSync(source.id)}
                    disabled={syncingId === source.id}
                    title="Sync now"
                  >
                    <SyncIcon fontSize="small" sx={{ animation: syncingId === source.id ? 'spin 1s linear infinite' : undefined, '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDisconnect(source.id)}
                    disabled={disconnectingId === source.id}
                    title="Disconnect"
                    sx={{ color: 'error.main' }}
                  >
                    <DisconnectIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function CalendarSourcesCard() {
  const [sources, setSources] = useState<CalendarSource[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<CalendarSource> | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const loadSources = async () => {
    try {
      const all = await calendarSourcesApi.getAll();
      // Show only ICS sources here (OAuth sources shown in ConnectCalendarCard)
      setSources(all.filter(s => !s.sourceType || s.sourceType === 'ICS'));
    } catch (err) {
      console.error('Failed to load calendar sources', err);
    }
  };

  useEffect(() => { loadSources(); }, []);

  const handleSave = async () => {
    if (!editItem?.name || !editItem?.icsUrl) return;
    try {
      if (editItem.id) {
        await calendarSourcesApi.update(editItem.id, editItem);
      } else {
        await calendarSourcesApi.create(editItem);
      }
      setDialogOpen(false);
      setEditItem(null);
      loadSources();
    } catch (err) {
      console.error('Failed to save calendar source', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this calendar source?')) return;
    try {
      await calendarSourcesApi.delete(id);
      loadSources();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleSync = async (id: string) => {
    setSyncingId(id);
    try {
      await calendarSourcesApi.sync(id);
      loadSources();
    } catch (err) {
      console.error('Failed to sync', err);
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">ICS Calendar Sources</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditItem({ name: '', icsUrl: '', isActive: true });
              setDialogOpen(true);
            }}
          >
            Add ICS Source
          </Button>
        </Box>
        {sources.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No ICS calendar sources configured</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>ICS URL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Synced</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.map((source) => (
                  <TableRow key={source.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{source.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {source.icsUrl.length > 50 ? source.icsUrl.substring(0, 50) + '...' : source.icsUrl}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={source.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={source.isActive ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {source.lastSyncedAt
                          ? format(new Date(source.lastSyncedAt), 'dd MMM HH:mm')
                          : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleSync(source.id)}
                        disabled={syncingId === source.id}
                        title="Sync"
                      >
                        <SyncIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setEditItem(source); setDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(source.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editItem?.id ? 'Edit Calendar Source' : 'Add Calendar Source'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                fullWidth
                value={editItem?.name || ''}
                onChange={e => setEditItem(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Work Calendar"
              />
              <TextField
                label="ICS URL"
                fullWidth
                value={editItem?.icsUrl || ''}
                onChange={e => setEditItem(prev => ({ ...prev, icsUrl: e.target.value }))}
                placeholder="https://example.com/calendar.ics"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editItem?.isActive ?? true}
                    onChange={e => setEditItem(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>Save</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
